<template>
  <div
    class="step"
    :class="classes"
  >
    <div class="step__header">
      <div
        class="h3 step__title"
        v-html="$props.stepTitle"
      />

      <basic-close-button
        v-if="$props.closeable"
        :size="closeButtonSize"
        @click="close"
      />
    </div>

    <basic-scrolling-with-gradient
      @scroll="handleScroll"
    >
      <div class="step__content">
        <slot />
      </div>
    </basic-scrolling-with-gradient>

    <slot name="footer">
      <modal-footer
        :alignement="$props.footerAlignement"

        :secondary-label="$props.secondaryLabel"
        :secondary-track-as="$props.secondaryTrackAs"
        :secondary-visible="$props.secondaryVisible"
        :primary-label="$props.primaryLabel"
        :primary-track-as="$props.primaryTrackAs"
        :primary-visible="$props.primaryVisible"
        :tertiary-label="$props.tertiaryLabel"
        
        @click-secondary="event => $emit('click-secondary', event)"
        @click-primary="event => $emit('click-primary', event)"
        @click-tertiary="event => $emit('click-tertiary', event)"
      />
    </slot>
  </div>
</template>

<script setup>
  import { computed, inject, onBeforeMount, provide, ref, getCurrentInstance } from 'vue'

  import events$ from '@shared/services/Events'

  import useBrowser from '@shared/hooks/useBrowser'

  import BasicCloseButton from '@shared/components/Basic/CloseButton'
  import ModalFooter from '@shared/components/Basic/ModalButBetter/Footer'
  import BasicScrollingWithGradient from '@shared/components/Basic/ScrollingWithGradient'

  import { EVENT_MODAL } from '@shared/config/events'

  // HOOKS
  const { isSmallOnlyViewport } = useBrowser()

  // INIT
  defineEmits(['click-primary', 'click-secondary', 'click-tertiary'])
  const props = defineProps({
    closeable: {
      type: Boolean,
      default: true
    },

    footerAlignement: {
      type: String,
      validator(value) {
        return ['left', 'center', 'right', 'spaced'].includes(value)
      },
      default: 'right'
    },

    id: {
      type: String,
      required: true
    },

    size: {
      validator(value) {
        return ['mini', 'midi', 'maxi', 'full'].includes(value)
      },
      default: 'midi',
    },

    stepTitle: {
      type: String,
      required: true
    },

    type: {
      /*
       *  passive:            no CTA
       *  transactional:      validate decisions, requires action to be completed and closed
       *  acknowledgement:    system requires the user to acknowledge the presented information
       *  progress:           navigate via next and back
       *  raw:
       */
      validator(value) {
        return ['passive', 'transactional', 'acknowledgement', 'progress', 'raw'].includes(value)
      },
      default: 'passive',
    },

    primaryLabel: {
      type: String,
      default: null,
    },

    primaryTrackAs: {
      type: String,
      default: null,
    },

    primaryVisible: {
      type: Boolean,
      default: true,
    },

    secondaryLabel: {
      type: String,
      default: null,
    },

    secondaryTrackAs: {
      type: String,
      default: null,
    },

    secondaryVisible: {
      type: Boolean,
      default: true,
    },

    tertiaryLabel: {
      type: String,
      default: null,
    },
  })

  // DATA
  const instance = ref(null)
  const modalOptions = inject('modalOptions')

  const fixedSizes = {
    fontSize: 24,
    padding: 36,
    totalSpace: 72,
    sizeDiff: 4
  }
  const styleVars = ref({
    fontSize: `${fixedSizes.fontSize}px`,
    padding: `${fixedSizes.padding}px`
  })

  // COMPUTED
  const classes = computed(() => {
    return {
      [`step--type-${props.type}`]: true,
      [`step--size-${props.size}`]: true,
    }
  })

  const closeButtonSize = computed(() => {
    return ['maxi', 'full'].includes(props.size) ? 'lg' : 'sm'
  })

  // METHODS
  /**
   * emit @on-close, when passed from parent. otherwise, requestClose is triggered
   */
  function close() {
    if (instance.value.vnode.props?.onOnClose) {
      instance.value.vnode.props.onOnClose()
    } else {
      modalOptions.modalRef.value.requestClose()
    }
  }

  function handleScroll(scrollTop) {
    if (!isSmallOnlyViewport.value) return

    if (scrollTop <= fixedSizes.totalSpace) {
      const percentage = 100 - parseInt((scrollTop / fixedSizes.totalSpace) * 100)

      styleVars.value.padding = `${fixedSizes.padding / 100 * percentage}px`
      styleVars.value.fontSize = `${fixedSizes.fontSize - (fixedSizes.sizeDiff - (fixedSizes.sizeDiff / 100 * percentage))}px`
    }
  }

  // LIFECYCLE HOOKS
  onBeforeMount(() => {
    instance.value = getCurrentInstance()

    events$.emit(EVENT_MODAL.UPDATE, {
      modalName: modalOptions.props.id + '-' + props.id,
    })
  })

  // EPILOGUE
  provide('modalOptions', Object.assign(
    {},
    modalOptions,
    {
      props
    }
  ))
</script>

<style scoped>
  .step {
    display: grid;
    grid-template-rows: min-content 1fr min-content min-content;

    &:has(.step__footer:empty) {
      grid-template-rows: min-content 1fr;

      .step__footer {
        display: none;
      }
    }

    .step__header {
      position: relative;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      max-width: 1544px;
      width: 100%;
      margin: 0 auto;
      padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-spacing-res-xs);
      text-align: left;

      .step__title {
        padding: 36px 0 0 0;
      }

      .step__title {
        padding: 36px 0 0 0;
      }

      .close-button {
        position: absolute;
        right: var(--dotcom-responsive-gutter-responsive);
        align-self: flex-start;
      }
    }

    .step__content {
      max-width: 1544px;
      margin: 0 auto;
      
    }

    .step__footer {
      /* styled inside footer-component */
    }

    &:deep(.scrollable) {
      scrollbar-gutter: stable;
      padding: 0;
    }
  }
</style>

<style scoped>
  @media (max-width: 671px) {
    .step {
      .step__header .step__title {
        padding: v-bind(styleVars.padding) 0 0 0;
        font-size: v-bind(styleVars.fontSize);
        transition: all var(--duration-medium-1) var(--ease-in-out-cubic);
      }
    }
  }

  @media (--v-small) {
    .step {
      max-height: 96dvh;

      &.step--size-mini {
        .step__header, .step__content {
          padding: var(--dotcom-responsive-gutter-responsive);
        }

        .step__footer {
          padding: calc(var(--dotcom-responsive-gutter-responsive) - var(--fixed-spacing-fix-06)) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive);
        }
      }

      &.step--size-midi {
        .step__header, .step__content {
          padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-offset-responsive);
        }

        .step__footer {
          padding: calc(var(--dotcom-responsive-gutter-responsive) - var(--fixed-spacing-fix-06)) var(--dotcom-responsive-offset-responsive) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-offset-responsive);
        }
      }

      &.step--size-maxi {
        .step__header, .step__content {
          padding: var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-offset-responsive);
        }

        .step__footer {
          padding: calc(var(--dotcom-responsive-gutter-responsive) - var(--fixed-spacing-fix-06)) var(--dotcom-responsive-offset-responsive) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-offset-responsive);
        }
      }
    }
  }

  @media (--v-medium) {
    .step {
      &.step--size-mini {
        max-height: 80dvh;
      }

      &.step--size-midi {
        max-height: 80dvh;

        .step__header, .step__content {
          padding: var(--dotcom-responsive-gutter-responsive);
        }

        .step__footer {
          padding: calc(var(--dotcom-responsive-gutter-responsive) - var(--fixed-spacing-fix-06)) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive) var(--dotcom-responsive-gutter-responsive);
        }
      }

      &.step--size-maxi {
        max-height: 96dvh;
      }
    }
  }
</style>